import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { LOGO_URL, NAV_LINKS } from "@/lib/site";
import { Link, useLocation } from "react-router-dom";
import Magnetic from "./Magnetic";

const scrollTo = (id) => {
  if (window.location.pathname !== "/") { window.location.href = `/#${id}`; return; }
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -80, duration: 1.2 });
  else el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export default function Nav({ ready }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <motion.header
        data-testid="site-nav"
        initial={{ y: -30, opacity: 0 }}
        animate={{ y: ready ? 0 : -30, opacity: ready ? 1 : 0 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
        className={`nav-shell fixed top-0 inset-x-0 z-[80] ${scrolled ? "shadow-[0_10px_30px_-24px_rgba(10,10,10,0.35)]" : ""}`}
      >
        <div className="mx-auto max-w-[1440px] px-5 md:px-10 h-16 md:h-20 flex items-center justify-between">
          <button
            data-testid="nav-logo"
            onClick={() => scrollTo("start")}
            className="flex items-center gap-3"
            aria-label="Zum Anfang"
          >
            <img src={LOGO_URL} alt="Hudak Enterprises" className="h-9 md:h-11 w-auto" />
          </button>

          <nav className="hidden lg:flex items-center gap-8">
            {NAV_LINKS.map((l) => (
              <button
                key={l.id}
                onClick={() => scrollTo(l.id)}
                data-testid={`nav-link-${l.id}`}
                className="nav-link"
              >
                {l.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <Magnetic strength={0.25}>
              <button
                data-testid="nav-cta"
                onClick={() => scrollTo("kontakt")}
                className="btn-primary hidden sm:inline-flex"
              >
                Kostenloses Erstgespräch <ArrowUpRight className="arrow" size={16} />
              </button>
            </Magnetic>
            <button
              onClick={() => setOpen(true)}
              data-testid="nav-menu-open"
              aria-label="Menü öffnen"
              className="lg:hidden w-11 h-11 rounded-full border border-black/10 flex items-center justify-center"
            >
              <Menu size={18} />
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[90] bg-[#0A0A0A] text-[#FAFAF7]"
          >
            <div className="flex items-center justify-between px-5 h-16">
              <img src={LOGO_URL} alt="Hudak" className="h-9 invert" />
              <button data-testid="nav-menu-close" onClick={() => setOpen(false)} className="w-11 h-11 rounded-full border border-white/15 flex items-center justify-center">
                <X size={18} />
              </button>
            </div>
            <div className="px-6 pt-10 flex flex-col gap-1">
              {NAV_LINKS.map((l, i) => (
                <motion.button
                  key={l.id}
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.05 * i, duration: 0.5 }}
                  onClick={() => { setOpen(false); setTimeout(() => scrollTo(l.id), 250); }}
                  className="text-left font-serif-editorial text-4xl py-3 border-b border-white/10 flex items-center justify-between"
                >
                  <span>{l.label}</span>
                  <ArrowUpRight size={22} className="text-[#C9A961]" />
                </motion.button>
              ))}
              <button
                onClick={() => { setOpen(false); setTimeout(() => scrollTo("kontakt"), 250); }}
                className="btn-gold mt-8 self-start"
              >
                Kostenloses Erstgespräch <ArrowUpRight size={16} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
